// src/pages/ClientListPage.tsx — Nexus-themed clients list
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/useSession';
import { GroupListCell } from '../components/group-list-cell';
import { DEFAULT_GROUP_MAP } from '@eotir/oidcbridge-shared/forum-groups';
import type {
  ListClientsResponse,
  TombstoneSummary,
} from '@eotir/oidcbridge-shared/api-contracts';

export function ClientListPage() {
  const { can } = useSession();
  const { data, isLoading, error } = useQuery({
    queryKey: ['clients'],
    queryFn: ({ signal }) => api<ListClientsResponse>('/api/clients', { signal }),
  });

  const { data: groupData } = useQuery({
    queryKey: ['forum-groups'],
    queryFn: ({ signal }) =>
      api<{ groupMap: Record<string, string>; source: string }>('/api/forum-groups', { signal }),
    staleTime: 5 * 60_000,
  });
  const groupMap = groupData?.groupMap ?? DEFAULT_GROUP_MAP;

  const clients = data?.clients ?? [];
  const totalURIs = clients.reduce((a, c) => a + c.redirectUris.length, 0);
  const totalGroups = new Set(clients.flatMap((c) => c.allowedGroups)).size;

  return (
    <div>
      <div className="eo-eyebrow">Authentication · Client Registry</div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="eo-h1">Clients</h1>
        {can('clients', 'C') && <Link to="/clients/new" className="eo-btn">＋ New Client</Link>}
      </div>

      {data && (
        <div className="grid grid-cols-4 gap-2.5 mb-6">
          <div className="eo-kpi"><div className="eo-kpi__n">{data.clients.length}</div><div className="eo-kpi__l">Active Clients</div></div>
          <div className="eo-kpi"><div className="eo-kpi__n">{totalURIs}</div><div className="eo-kpi__l">Redirect URIs</div></div>
          <div className="eo-kpi"><div className="eo-kpi__n">{totalGroups}</div><div className="eo-kpi__l">Referenced Groups</div></div>
          <div className="eo-kpi"><div className="eo-kpi__n">{data.tombstones.length}</div><div className="eo-kpi__l">Tombstoned</div></div>
        </div>
      )}

      {isLoading && <p className="text-sm text-ink-3">Loading…</p>}
      {error && <p className="text-sm text-err">Failed to load: {(error as Error).message}</p>}

      {data && (
        <>
          <section className="mb-8">
            <h2 className="eo-h2">
              ◆ Active <span className="text-ink-4 ml-2">{data.clients.length} of {data.totalCount}</span>
            </h2>
            {data.clients.length === 0 ? (
              <p className="text-sm text-ink-3">No clients yet.</p>
            ) : (
              <table className="eo-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Redirect URIs</th>
                    <th>Group policy</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.clients.map((c) => (
                    <tr key={c.clientId}>
                      <td>
                        <span className="eo-mono">{c.clientId}</span>
                        {c.displayName && (
                          <div className="text-[11.5px] text-ink-3 mt-0.5">{c.displayName}</div>
                        )}
                      </td>
                      <td>
                        <span className="eo-chip eo-chip--ops">
                          {c.redirectUris.length} URI{c.redirectUris.length === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td>
                        <GroupListCell
                          groups={c.allowedGroups}
                          groupMode={c.groupMode}
                          groupMap={groupMap}
                        />
                      </td>
                      <td className="text-right">
                        <Link to={`/clients/${c.clientId}`}
                              className="font-mono text-[11px] tracking-label uppercase text-crimson-hover no-underline hover:text-white">
                          Details →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {data.tombstones.length > 0 && (
            <TombstoneSection tombstones={data.tombstones} />
          )}
        </>
      )}
    </div>
  );
}

function TombstoneSection({ tombstones }: { tombstones: TombstoneSummary[] }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const now = Date.now();
  const expiredCount = tombstones.filter((t) => {
    const ms = Date.parse(t.hardDeleteAt);
    return Number.isNaN(ms) || ms <= now;
  }).length;

  const sweepMut = useMutation({
    mutationFn: () =>
      api<{ purged: string[]; failed: Array<{ clientId: string; reason: string }> }>(
        '/api/tombstones/sweep',
        { method: 'POST' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });

  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="eo-h2">
          ◆ Tombstoned <span className="text-ink-4 ml-2">{tombstones.length}</span>
        </h2>
        {can('tombstones', 'D') && expiredCount > 0 && (
          <button
            type="button"
            disabled={sweepMut.isPending}
            onClick={() => {
              if (window.confirm(
                `Permanently purge ${expiredCount} expired tombstone${expiredCount === 1 ? '' : 's'}? ` +
                'Their Vault secrets are deleted too. This cannot be undone.',
              )) {
                sweepMut.mutate();
              }
            }}
            className="eo-btn eo-btn--warn"
          >
            {sweepMut.isPending ? 'Sweeping…' : `Sweep ${expiredCount} expired`}
          </button>
        )}
      </div>
      {sweepMut.data && sweepMut.data.failed.length > 0 && (
        <p className="text-sm text-err mt-1">
          Failed: {sweepMut.data.failed.map((f) => `${f.clientId} (${f.reason})`).join('; ')}
        </p>
      )}
      {sweepMut.error && (
        <p className="text-sm text-err mt-1">{(sweepMut.error as Error).message}</p>
      )}
      <table className="eo-table">
        <thead>
          <tr>
            <th>Client ID</th>
            <th>Deleted</th>
            <th>Hard-delete by</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tombstones.map((t) => <TombstoneRow key={t.clientId} t={t} />)}
        </tbody>
      </table>
    </section>
  );
}

function TombstoneRow({ t }: { t: TombstoneSummary }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const restoreMut = useMutation({
    mutationFn: () =>
      api<{ client: { clientId: string } }>(`/api/clients/${t.clientId}/restore`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
  const purgeMut = useMutation({
    mutationFn: () =>
      api<{ ok: true; purged: string }>(`/api/clients/${t.clientId}/purge`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
  const expired = (() => {
    const ms = Date.parse(t.hardDeleteAt);
    return Number.isNaN(ms) || ms <= Date.now();
  })();

  return (
    <tr>
      <td><span className="eo-mono">{t.clientId}</span></td>
      <td className="text-ink-3">{new Date(t.deletedAt).toLocaleString()}</td>
      <td className={expired ? 'text-err-soft' : 'text-warn'}>
        {new Date(t.hardDeleteAt).toLocaleString()}
        {expired && <span className="eo-chip eo-chip--err ml-2">expired</span>}
      </td>
      <td className="text-right space-x-2">
        {can('tombstones', 'U') && !expired && (
          <button
            type="button"
            disabled={restoreMut.isPending || purgeMut.isPending}
            onClick={() => {
              if (window.confirm(`Restore client '${t.clientId}'? It will start authenticating again.`)) {
                restoreMut.mutate();
              }
            }}
            className="eo-btn eo-btn--ghost"
          >
            {restoreMut.isPending ? 'Restoring…' : 'Restore'}
          </button>
        )}
        {can('tombstones', 'D') && (
          <button
            type="button"
            disabled={purgeMut.isPending || restoreMut.isPending}
            onClick={() => {
              const typed = window.prompt(
                `Permanently purge '${t.clientId}' and delete its Vault secret? ` +
                `This cannot be undone. Type the client id to confirm:`,
              );
              if (typed === t.clientId) {
                purgeMut.mutate();
              } else if (typed !== null) {
                window.alert('Client id did not match — purge cancelled.');
              }
            }}
            className="eo-btn eo-btn--err"
          >
            {purgeMut.isPending ? 'Purging…' : 'Purge now'}
          </button>
        )}
        {(restoreMut.error || purgeMut.error) && (
          <span className="ml-2 text-xs text-err">
            {((restoreMut.error ?? purgeMut.error) as Error).message}
          </span>
        )}
      </td>
    </tr>
  );
}
